import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Switch, FormControlLabel, TextField, Button, Paper,
  Alert, Container, Grid, Card, CardContent, Divider, CircularProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Save } from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';
import { getAdminAuthHeaders } from './adminApi';

function DataAdmin() {
  const { t } = useAdminLanguage();
  const [settings, setSettings] = useState({
    reports: {
      autoDelete: false,
      retentionDays: 365
    },
    logs: {
      autoDelete: false,
      retentionDays: 90
    },
    statistics: {
      autoDelete: false,
      retentionDays: 365
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Charger les paramètres actuels
  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/dataretention');
      setSettings(response.data);
    } catch (error) {
      setErrorMessage('Erreur lors du chargement des paramètres de rétention des données.');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Gérer les changements de switch
  const handleSwitchChange = (category, checked) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        autoDelete: checked
      }
    }));
  };

  // Gérer les changements de nombre de jours
  const handleDaysChange = (category, value) => {
    const days = parseInt(value);
    if (!isNaN(days) && days > 0) {
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          retentionDays: days
        }
      }));
    }
  };

  // Sauvegarder les paramètres
  const handleSave = async () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await axios.put('/api/dataretention', settings, { headers: getAdminAuthHeaders() });
      setSuccessMessage('Paramètres de rétention des données sauvegardés avec succès.');
    } catch (error) {
      setErrorMessage('Erreur lors de la sauvegarde des paramètres.');
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Options prédéfinies pour les jours de rétention
  const retentionOptions = {
    reports: [
      { value: 30, label: '30 jours (1 mois)' },
      { value: 90, label: '90 jours (3 mois)' },
      { value: 180, label: '180 jours (6 mois)' },
      { value: 365, label: '365 jours (1 an)' },
      { value: 730, label: '730 jours (2 ans)' },
      { value: 1095, label: '1095 jours (3 ans)' },
      { value: 1825, label: '1825 jours (5 ans)' },
      { value: 3650, label: '3650 jours (10 ans)' }
    ],
    logs: [
      { value: 30, label: '30 jours (1 mois)' },
      { value: 90, label: '90 jours (3 mois)' },
      { value: 180, label: '180 jours (6 mois)' },
      { value: 365, label: '365 jours (1 an)' },
      { value: 730, label: '730 jours (2 ans)' },
      { value: 1095, label: '1095 jours (3 ans)' },
      { value: 1825, label: '1825 jours (5 ans)' },
      { value: 3650, label: '3650 jours (10 ans)' }
    ],
    statistics: [
      { value: 30, label: '30 jours (1 mois)' },
      { value: 90, label: '90 jours (3 mois)' },
      { value: 180, label: '180 jours (6 mois)' },
      { value: 365, label: '365 jours (1 an)' },
      { value: 730, label: '730 jours (2 ans)' },
      { value: 1095, label: '1095 jours (3 ans)' },
      { value: 1825, label: '1825 jours (5 ans)' },
      { value: 3650, label: '3650 jours (10 ans)' }
    ]
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        color="primary"
        sx={{ fontWeight: 'bold', mb: 4 }}
      >
        {t('Gestion des données')}
      </Typography>

      <Info type="data" />

      {errorMessage && <Alert severity="error" sx={{ mb: 3 }}>{errorMessage}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}

      <Grid container spacing={3}>
        {/* Gestion des procès-verbaux */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {t('Procès-verbaux')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.reports.autoDelete}
                      onChange={(e) => handleSwitchChange('reports', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      {t('Supprimer automatiquement les procès-verbaux')}
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                  {settings.reports.autoDelete 
                    ? t('Les procès-verbaux seront supprimés automatiquement après la période définie.')
                    : t('Les procès-verbaux sont conservés indéfiniment.')}
                </Typography>
              </Box>

              {settings.reports.autoDelete && (
                <Box sx={{ ml: 4 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('Période de conservation')}</InputLabel>
                    <Select
                      value={settings.reports.retentionDays}
                      label={t('Période de conservation')}
                      onChange={(e) => handleDaysChange('reports', e.target.value)}
                    >
                      {retentionOptions.reports.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label={t('Nombre de jours personnalisé')}
                    type="number"
                    value={settings.reports.retentionDays}
                    onChange={(e) => handleDaysChange('reports', e.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText={t('Entrez un nombre de jours personnalisé si les options prédéfinies ne conviennent pas')}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Gestion des logs */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {t('Journalisation (Logs)')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.logs.autoDelete}
                      onChange={(e) => handleSwitchChange('logs', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      {t('Supprimer automatiquement les logs')}
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                  {settings.logs.autoDelete 
                    ? t('Les logs de connexion seront supprimés automatiquement après la période définie.')
                    : t('Les logs de connexion sont conservés indéfiniment.')}
                </Typography>
              </Box>

              {settings.logs.autoDelete && (
                <Box sx={{ ml: 4 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('Période de conservation')}</InputLabel>
                    <Select
                      value={settings.logs.retentionDays}
                      label={t('Période de conservation')}
                      onChange={(e) => handleDaysChange('logs', e.target.value)}
                    >
                      {retentionOptions.logs.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label={t('Nombre de jours personnalisé')}
                    type="number"
                    value={settings.logs.retentionDays}
                    onChange={(e) => handleDaysChange('logs', e.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText={t('Entrez un nombre de jours personnalisé si les options prédéfinies ne conviennent pas')}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Gestion des statistiques */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {t('Statistiques utilisateurs')}
              </Typography>
              
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="body2">
                  {t("Les statistiques n'enregistrent que des compteurs anonymes (nombre de documents modifiés, rubriques générées, mots générés). Aucun contenu de document, titre ou information sensible n'est stocké. Ces données ne sont pas considérées comme sensibles.")}
                </Typography>
              </Paper>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.statistics?.autoDelete || false}
                      onChange={(e) => handleSwitchChange('statistics', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      {t('Supprimer automatiquement les statistiques')}
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                  {settings.statistics?.autoDelete 
                    ? t('Les statistiques utilisateurs seront supprimées automatiquement après la période définie.')
                    : t('Les statistiques utilisateurs sont conservées indéfiniment.')}
                </Typography>
              </Box>

              {settings.statistics?.autoDelete && (
                <Box sx={{ ml: 4 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('Période de conservation')}</InputLabel>
                    <Select
                      value={settings.statistics?.retentionDays || 365}
                      label={t('Période de conservation')}
                      onChange={(e) => handleDaysChange('statistics', e.target.value)}
                    >
                      {retentionOptions.statistics.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label={t('Nombre de jours personnalisé')}
                    type="number"
                    value={settings.statistics?.retentionDays || 365}
                    onChange={(e) => handleDaysChange('statistics', e.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText={t('Entrez un nombre de jours personnalisé si les options prédéfinies ne conviennent pas')}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Résumé et bouton de sauvegarde */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              {t('Résumé de la configuration')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('Procès-verbaux')} :</strong> {settings.reports.autoDelete 
                ? t('Suppression automatique après {days} jours', { days: settings.reports.retentionDays })
                : t('Conservation indéfinie')}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('Logs de connexion')} :</strong> {settings.logs.autoDelete 
                ? t('Suppression automatique après {days} jours', { days: settings.logs.retentionDays })
                : t('Conservation indéfinie')}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3 }}>
              <strong>{t('Statistiques')} :</strong> {settings.statistics?.autoDelete 
                ? t('Suppression automatique après {days} jours', { days: settings.statistics?.retentionDays || 365 })
                : t('Conservation indéfinie')}
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {saving ? t('Sauvegarde en cours...') : t('Sauvegarder les paramètres')}
            </Button>
          </Paper>
        </Grid>
      </Grid>

    </Container>
  );
}

export default DataAdmin;